import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import _ from "lodash";

import { Button } from "@showitinc/showit-wc";
import { useAccountState } from "../hooks/query/useAccount";
import Glyphicon from "./base/Glyphicon";
import {
  SCALE_PADDING,
  PREVIEW_GAP_PADDING,
  CSS_PROPERTIES,
} from "../constants/LibrarySitePreviewConstants";
import styles from "./LibrarySitePreview.module.css";

const {
  DESKTOP_WIDTH,
  DESKTOP_HEIGHT,
  SCREEN_WIDTH,
  BEZEL_TOP,
  BEZEL_BOTTOM,
  DESKTOP_SCALE,
  DESKTOP_IFRAME_WIDTH,
  MOBILE_SCALE,
  MOBILE_IFRAME_WIDTH,
} = CSS_PROPERTIES;

/**
 * Get the preview content dimensions
 * @param {React.RefObject} reference - The reference to the wrapper element
 * @returns {Object}
 */
const getPreviewContentDimensions = (reference) => {
  const wrapperEl = reference.current;
  return {
    width: wrapperEl.clientWidth,
    height: wrapperEl.clientHeight,
  };
};

/**
 * LibrarySitePreview renders the mobile and desktop previews of a design and a CTA footer.
 *
 * @typedef {Object} LibrarySitePreviewProps
 * @property {Object} [site] - Site metadata used for display (ex: name, desc).
 * @property {function} [onSelect] - When call-to-action button is pressed.
 * @property {function} [onClose] - When close buttons are pressed.
 * @property {string} [designURL] - URL to load in the preview.
 * @property {string} [buttonText] - Call-to-action text, defaults to "Start with this design".
 * @property {string} [modal] - Context in which the preview is shown (ex: "import").
 */
const LibrarySitePreview = ({ site, onSelect, onClose, designURL, buttonText, modal }) => {
  const intercomOpen = !!useAccountState().get("intercomOpen", false);
  const previewContentRef = useRef(null);
  const mobileIframeRef = useRef(null);
  const desktopIframeRef = useRef(null);
  const desktopWrapperRef = useRef(null);
  const mobileWrapperRef = useRef(null);
  const mobilePreviewRef = useRef(null);

  const finalButtonText = buttonText ?? "Start with this design";
  const siteName = _.get(site, "name", "");
  const desc = _.get(site, "desc", false);

  /* 
  Logic to scale mobile and desktop previews
  This could be done completely in CSS, but due to compatibility issues with Safari 18.1
  and earlier versions, we need to use JavaScript to scale the mobile/desktop previews.
   */
  useLayoutEffect(() => {
    const mobileIframe = mobileIframeRef.current;
    const desktopIframe = desktopIframeRef.current;

    if (!mobileIframe && !desktopIframe) return undefined;
    const mobileWrapperEl = mobileWrapperRef.current;
    const desktopWrapperEl = desktopWrapperRef.current;

    const desktopCS = getComputedStyle(desktopWrapperEl);
    const DESKTOP_W = parseFloat(desktopCS.getPropertyValue(DESKTOP_WIDTH));
    const DESKTOP_H = parseFloat(desktopCS.getPropertyValue(DESKTOP_HEIGHT));

    // Accounting for mobile preview border bars
    const mobilePreviewEl = mobilePreviewRef.current;
    const mobileCS = getComputedStyle(mobilePreviewEl);
    const MOBILE_W = parseFloat(mobileCS.getPropertyValue(SCREEN_WIDTH)) + SCALE_PADDING;
    const MOBILE_BEZEL_TOP = parseFloat(mobileCS.getPropertyValue(BEZEL_TOP));
    const MOBILE_BEZEL_BOTTOM = parseFloat(mobileCS.getPropertyValue(BEZEL_BOTTOM));
    const MOBILE_H =
      parseFloat(mobileCS.getPropertyValue(SCREEN_WIDTH)) + MOBILE_BEZEL_TOP + MOBILE_BEZEL_BOTTOM;

    const updateScales = () => {
      const previewContentEl = previewContentRef.current;
      // Desktop scaling
      const { width: desktopWidth, height: desktopHeight } =
        getPreviewContentDimensions(desktopWrapperRef);
      const desktopScale = Math.min(
        desktopWidth / (DESKTOP_W + SCALE_PADDING),
        desktopHeight / (DESKTOP_H + SCALE_PADDING),
        1
      );
      desktopWrapperEl.style.setProperty(DESKTOP_SCALE, String(desktopScale));
      previewContentEl.style.setProperty(
        DESKTOP_IFRAME_WIDTH,
        String((previewContentEl.clientHeight - PREVIEW_GAP_PADDING) * (16 / 9)) + "px"
      );

      // Mobile scaling
      const { width: mobileWidth, height: mobileHeight } =
        getPreviewContentDimensions(mobileWrapperRef);
      const mobileScale = Math.min(mobileWidth / MOBILE_W, mobileHeight / MOBILE_H, 1);
      mobilePreviewEl.style.setProperty(MOBILE_SCALE, String(mobileScale));
      previewContentEl.style.setProperty(
        MOBILE_IFRAME_WIDTH,
        String((previewContentEl.clientHeight - PREVIEW_GAP_PADDING) * (2 / 3)) + "px"
      );
    };

    updateScales();

    // Re-update scale size for mobile and desktop previews on resize. Deferred
    // a frame: updateScales resizes the observed wrappers, and doing that inside
    // the callback triggers the "ResizeObserver loop" error
    let scaleRAF = 0;
    const resizeObserver = new ResizeObserver(() => {
      if (scaleRAF) return;
      scaleRAF = requestAnimationFrame(() => {
        scaleRAF = 0;
        updateScales();
      });
    });
    if (mobileWrapperEl) resizeObserver.observe(mobileWrapperEl);
    if (desktopWrapperEl) resizeObserver.observe(desktopWrapperEl);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(scaleRAF);
    };
  }, []);

  return (
    <div className={`site-preview ${styles["site-preview-container"]}`}>
      {modal !== "import" && (
        <div className={styles["site-preview-header-container"]}>
          <button className={styles["back-button"]} onClick={onClose}>
            Back to designs
          </button>
          <Glyphicon glyph="remove_2" className={styles["close-button"]} onClick={onClose} />
        </div>
      )}

      <div className={styles["preview-content"]} ref={previewContentRef}>
        <div className={styles["mobile-wrapper"]} ref={mobileWrapperRef}>
          <div className={styles["mobile-preview"]} ref={mobilePreviewRef}>
            <div className={styles["mobile-preview-inner"]}>
              <iframe
                ref={mobileIframeRef}
                src={designURL}
                className={styles["mobile-iframe"]}
                title="mobile-preview-frame"
              />
            </div>
          </div>
        </div>
        <div className={styles["desktop-preview"]}>
          <div className={styles["desktop-wrapper"]} ref={desktopWrapperRef}>
            <div className={styles["desktop-preview-inner"]}>
              <iframe
                ref={desktopIframeRef}
                src={designURL}
                className={styles["desktop-iframe"]}
                title="desktop-preview-frame"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles["description-panel"]}>
        <div className={styles["description-panel-inner"]}>
          <div className={styles["description-title-section"]}>
            <h1>{siteName}</h1>
            {desc ? <h2>{desc}</h2> : null}
          </div>
          <div
            className={`${styles["button-container"]} ${
              intercomOpen ? styles["intercom-open"] : ""
            }`}
          >
            <Button size="lg" variant="feature" onClick={onSelect} className={styles["cta-button"]}>
              {finalButtonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibrarySitePreview;
